import React from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Chip,
    Box,
    Card,
    CardContent,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Home as HomeIcon,
    CalendarToday as CalendarIcon,
    People as PeopleIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';

import { formatDateWithIntl } from '@shared/utils/getFormatDate';

import { getStatusChipMeta, statusVariant } from '../lib/reservationRules';

const ReservationDetailModal = ({ open, onClose, reservation, t }) => {
    const getStatusColor = (status) => statusVariant(status);

    const getStatusIcon = (status) => {
        const variant = statusVariant(status);
        if (variant === 'success') return <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />;
        if (variant === 'warning') return <PendingIcon sx={{ fontSize: 16, mr: 0.5 }} />;
        return <CancelIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    };

    const statusLabel = (status) => (status ? t(getStatusChipMeta(status).labelKey) : t('unknown'));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.20)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: 'grey.50',
                    color: 'text.primary',
                    position: 'relative',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        pr: 5,
                        color: 'text.primary'
                    }}
                >
                    <HomeIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                    {t('reservationDetails')}
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'text.secondary',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {reservation && (
                    <Box sx={{ p: 3 }}>
                        <Card
                            elevation={0}
                            sx={{
                                mb: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{
                                        fontWeight: '600',
                                        color: 'text.primary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 2.5
                                    }}
                                >
                                    <HomeIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                                    {t('generalInformation')}
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 2,
                                                backgroundColor: 'grey.50',
                                                borderRadius: 2,
                                                mb: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <HomeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    {t('property')}
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: '600'}} color="text.primary">
                                                    {reservation.property}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 2,
                                                backgroundColor: 'grey.50',
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'success.light',
                                                borderLeftWidth: 4,
                                                borderLeftColor: 'success.main',
                                            }}
                                        >
                                            <CalendarIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    {t('startDate')}
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: '600'}} color="text.primary">
                                                    {formatDateWithIntl(reservation.startDate, 'medium')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 2,
                                                backgroundColor: 'grey.50',
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'error.light',
                                                borderLeftWidth: 4,
                                                borderLeftColor: 'error.main',
                                            }}
                                        >
                                            <CalendarIcon sx={{ fontSize: 20, color: 'error.main' }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    {t('endDate')}
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: '600'}} color="text.primary">
                                                    {formatDateWithIntl(reservation.endDate, 'medium')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 2,
                                                backgroundColor: 'grey.50',
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'info.light',
                                                borderLeftWidth: 4,
                                                borderLeftColor: 'info.main',
                                            }}
                                        >
                                            <PeopleIcon sx={{ fontSize: 20, color: 'info.main' }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    {t('numberOfGuests')}
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: '600'}} color="text.primary">
                                                    {reservation.totalPeople} {reservation.totalPeople === 1 ? t('person') : t('people')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{
                                        fontWeight: '600',
                                        color: 'text.primary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 2.5
                                    }}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                                    {t('statusAndApproval')}
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}>
                                        <Box
                                            sx={{
                                                p: 2.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                backgroundColor: 'grey.50',
                                                textAlign: 'center',
                                                borderLeftWidth: 4,
                                                borderLeftColor: `${getStatusColor(reservation.status)}.main`,
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                                                {t('approvalStatusCEO')}
                                            </Typography>
                                            <Chip
                                                icon={getStatusIcon(reservation.status)}
                                                label={statusLabel(reservation.status)}
                                                color={getStatusColor(reservation.status)}
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: '600',
                                                    fontSize: '0.875rem',
                                                    height: 36,
                                                    borderRadius: 3,
                                                    backgroundColor: 'white',
                                                    '& .MuiChip-label': {
                                                        px: 1.5,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}>
                                        <Box
                                            sx={{
                                                p: 2.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                backgroundColor: 'grey.50',
                                                textAlign: 'center',
                                                borderLeftWidth: 4,
                                                borderLeftColor: `${getStatusColor(reservation.managerApprovalStatus)}.main`,
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                                                {t('approvalStatus')}
                                            </Typography>
                                            <Tooltip title={statusLabel(reservation.managerApprovalStatus)} arrow>
                                                <Chip
                                                    icon={getStatusIcon(reservation.managerApprovalStatus)}
                                                    label={statusLabel(reservation.managerApprovalStatus)}
                                                    color={getStatusColor(reservation.managerApprovalStatus)}
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: '600',
                                                        fontSize: '0.875rem',
                                                        height: 36,
                                                        borderRadius: 3,
                                                        backgroundColor: 'white',
                                                        '& .MuiChip-label': {
                                                            px: 1.5,
                                                            maxWidth: '180px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        },
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2.5,
                    backgroundColor: 'grey.50',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                        minWidth: 120,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: '600',
                        textTransform: 'none',
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                        },
                    }}
                >
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ReservationDetailModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    reservation: PropTypes.object,
    t: PropTypes.func.isRequired,
    statusChip: PropTypes.func,
};

export default ReservationDetailModal;
